import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TeeIconButton } from '@/components/ui/tee-icon-button';
import type { AssistantRef } from '@/types/support-assistant';
import { AssistantTable, isTableLine, parseTable } from './AssistantTable';

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  return line.split(INLINE).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={key}
          className="rounded-[4px] bg-[var(--background-page-header)] px-1 text-[14px]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={key}>{part}</span>;
  });
}

const NUMBERED = /^\s*(\d{1,2})[.)]\s+(.*)$/;
const NUMBER_CLASS =
  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-gold-500)] font-[family-name:var(--type-font-family-heading)] text-[11px] font-bold leading-none text-[var(--text-on-accent)]';

export interface AssistantMessageTextProps {
  text: string;
  caret?: boolean;
  refs?: AssistantRef[];
  onRefFocus?: (n: number) => void;
  onRefActivate?: (n: number) => void;
}

/**
 * Light markdown (bold, bullet lists, inline code, numbered findings) rendered to React elements.
 * Never injects HTML, so model or CRM text cannot execute markup.
 */
export function AssistantMessageText({
  text,
  caret = false,
  refs,
  onRefFocus,
  onRefActivate,
}: AssistantMessageTextProps) {
  const { t } = useTranslation();
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let findings: { n: number; body: string }[] = [];

  const flush = (idx: number) => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${idx}`} className="list-disc pl-6">
          {bullets.map((b, i) => (
            <li key={`li-${idx}-${i}`}>{renderInline(b, `li-${idx}-${i}`)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    }
    if (findings.length) {
      blocks.push(
        <div key={`fd-${idx}`} className="flex flex-col gap-2" data-testid="findings">
          {findings.map((f, i) => {
            const hasRef = !!refs?.some((r) => r.n === f.n);
            const plain = f.body.replace(/\*\*/g, '');
            return (
              <div key={`fd-${idx}-${i}`} className="flex items-start gap-2">
                {hasRef ? (
                  <span
                    className="mt-[1px] inline-flex"
                    onMouseEnter={() => onRefFocus?.(f.n)}
                    onFocus={() => onRefFocus?.(f.n)}
                  >
                    <TeeIconButton
                      icon={<span aria-hidden="true">{f.n}</span>}
                      aria-label={t('assistant.thread.showReference', {
                        n: f.n,
                        text: plain,
                      })}
                      onClick={() => onRefActivate?.(f.n)}
                      className={`${NUMBER_CLASS} p-0 max-h-5 hover:bg-[var(--color-gold-500)] hover:text-[var(--text-on-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]`}
                    />
                  </span>
                ) : (
                  <span aria-hidden="true" className={`${NUMBER_CLASS} mt-[1px]`}>
                    {f.n}
                  </span>
                )}
                <span className="min-w-0">{renderInline(f.body, `fd-${idx}-${i}`)}</span>
              </div>
            );
          })}
        </div>,
      );
      findings = [];
    }
  };

  let tableLines: string[] = [];
  const flushTable = (idx: number) => {
    if (!tableLines.length) return;
    const parsed = parseTable(tableLines);
    if (parsed) {
      blocks.push(
        <AssistantTable
          key={`tb-${idx}`}
          table={parsed}
          renderInline={renderInline}
          keyPrefix={`tb-${idx}`}
        />,
      );
    } else {
      tableLines.forEach((l, i) =>
        blocks.push(<p key={`tp-${idx}-${i}`}>{renderInline(l, `tp-${idx}-${i}`)}</p>),
      );
    }
    tableLines = [];
  };

  lines.forEach((line, idx) => {
    if (isTableLine(line)) {
      flush(idx);
      tableLines.push(line);
      return;
    }
    flushTable(idx);
    const num = NUMBERED.exec(line);
    if (num) {
      if (bullets.length) flush(idx);
      findings.push({ n: Number(num[1]), body: num[2] });
      return;
    }
    const m = /^\s*[-*]\s+(.*)$/.exec(line);
    if (m) {
      if (findings.length) flush(idx);
      bullets.push(m[1]);
      return;
    }
    flush(idx);
    if (line.trim()) blocks.push(<p key={`p-${idx}`}>{renderInline(line, `p-${idx}`)}</p>);
  });
  flush(lines.length);
  flushTable(lines.length);

  return (
    <div className="flex flex-col gap-2 whitespace-pre-wrap text-[14px] font-normal leading-[1.5] text-[var(--text-content-title)]">
      {blocks}
      {caret && (
        <span
          aria-hidden="true"
          data-testid="stream-caret"
          className="inline-block h-4 w-[2px] animate-pulse bg-[var(--color-forest)] motion-reduce:animate-none"
        />
      )}
    </div>
  );
}
